import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Image as ImageIcon, Video, Mic, FileText, X, Send, Smile, Hash, AtSign, Users, Globe, ChevronDown, ChevronUp, Move } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

interface PostComposerProps {
  onPostCreated: () => void;
  theme: 'light' | 'dark';
}

export default function PostComposer({ onPostCreated, theme }: PostComposerProps) {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState<'text' | 'image' | 'video' | 'podcast' | 'article'>('text');
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviews, setMediaPreviews] = useState<string[]>([]);
  const [visibility, setVisibility] = useState<'community' | 'public'>('community');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [userPhotoUrl, setUserPhotoUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mediaPositions, setMediaPositions] = useState<{ x: number; y: number }[]>([]);
  const [adjustingIndex, setAdjustingIndex] = useState<number | null>(null);
  const mediaDragRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isDraggingMedia = useRef(false);
  const mediaDragStart = useRef({ x: 0, y: 0, posX: 50, posY: 50, idx: 0 });

  useEffect(() => {
    fetchUserPhoto();
  }, [user?.id]);

  const fetchUserPhoto = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select('photo_url')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data?.photo_url) {
        if (data.photo_url.startsWith('http://') || data.photo_url.startsWith('https://')) {
          setUserPhotoUrl(data.photo_url);
        } else {
          const { data: urlData } = supabase.storage
            .from('profile-photos')
            .getPublicUrl(data.photo_url);
          setUserPhotoUrl(urlData.publicUrl);
        }
      }
    } catch (error) {
      console.error('Error fetching user photo:', error);
    }
  };

  const handleMediaDragStart = useCallback((e: React.MouseEvent | React.TouchEvent, idx: number) => {
    e.preventDefault();
    isDraggingMedia.current = true;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const pos = mediaPositions[idx] ?? { x: 50, y: 50 };
    mediaDragStart.current = { x: clientX, y: clientY, posX: pos.x, posY: pos.y, idx };

    const onMove = (ev: MouseEvent | TouchEvent) => {
      if (!isDraggingMedia.current) return;
      const el = mediaDragRefs.current[idx];
      if (!el) return;
      const cx = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
      const cy = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
      const rect = el.getBoundingClientRect();
      const dx = cx - mediaDragStart.current.x;
      const dy = cy - mediaDragStart.current.y;
      const newX = Math.min(100, Math.max(0, mediaDragStart.current.posX - (dx / rect.width) * 100));
      const newY = Math.min(100, Math.max(0, mediaDragStart.current.posY - (dy / rect.height) * 100));
      setMediaPositions(prev => {
        const next = [...prev];
        next[idx] = { x: Math.round(newX), y: Math.round(newY) };
        return next;
      });
    };
    const onUp = () => {
      isDraggingMedia.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, [mediaPositions]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video' | 'podcast' | 'article') => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setPostType(type);
    setMediaFiles(files);

    const previews = files.map(file => URL.createObjectURL(file));
    setMediaPreviews(previews);
    setMediaPositions(files.map(() => ({ x: 50, y: 50 })));
    setAdjustingIndex(null);
  };

  const removeMedia = (index: number) => {
    const newFiles = mediaFiles.filter((_, i) => i !== index);
    const newPreviews = mediaPreviews.filter((_, i) => i !== index);
    const newPositions = mediaPositions.filter((_, i) => i !== index);

    URL.revokeObjectURL(mediaPreviews[index]);

    setMediaFiles(newFiles);
    setMediaPreviews(newPreviews);
    setMediaPositions(newPositions);

    if (newFiles.length === 0) {
      setPostType('text');
      setAdjustingIndex(null);
    }
  };

  const uploadMedia = async (): Promise<string[]> => {
    const uploadedUrls: string[] = [];

    for (const file of mediaFiles) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user!.id}/${Date.now()}-${Math.random()}.${fileExt}`;

      const { error: uploadError, data } = await supabase.storage
        .from('community-media')
        .upload(fileName, file);

      if (uploadError) {
        console.error('Error uploading file:', uploadError);
        continue;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('community-media')
        .getPublicUrl(fileName);

      uploadedUrls.push(publicUrl);
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim() && mediaFiles.length === 0) return;

    setIsSubmitting(true);

    try {
      let mediaUrls: string[] = [];
      let mediaType: string | null = null;

      if (mediaFiles.length > 0) {
        mediaUrls = await uploadMedia();

        if (postType === 'image') mediaType = 'image';
        else if (postType === 'video') mediaType = 'video';
        else if (postType === 'podcast') mediaType = 'audio';
        else if (postType === 'article') mediaType = 'document';
      }

      const hashtags = content.match(/#\w+/g)?.map(tag => tag.substring(1)) || [];

      const { error } = await supabase
        .from('consultant_community_posts')
        .insert({
          consultant_id: user!.id,
          content: content.trim(),
          post_type: postType,
          media_urls: mediaUrls,
          media_type: mediaType,
          hashtags: hashtags,
          visibility: visibility,
          media_positions: mediaPositions.length > 0 ? mediaPositions : [],
        });

      if (error) throw error;

      setContent('');
      setMediaFiles([]);
      setMediaPreviews([]);
      setMediaPositions([]);
      setPostType('text');
      setVisibility('community');
      setIsExpanded(false);
      setAdjustingIndex(null);
      onPostCreated();
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const commonEmojis = ['👍', '❤️', '😊', '🎉', '🔥', '💯', '🚀', '💡', '✨', '🎯'];

  return (
    <div className={`rounded-xl shadow-lg overflow-hidden ${
      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
    }`}>
      <form onSubmit={handleSubmit}>
        {!isExpanded ? (
          <div
            onClick={() => setIsExpanded(true)}
            className={`p-4 cursor-pointer transition-colors ${
              theme === 'dark' ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center space-x-2">
                <label
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-gray-700 text-gray-300'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ImageIcon className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium">Photo</span>
                </label>

                <label
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-gray-700 text-gray-300'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Video className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium">Video</span>
                </label>

                <label
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-gray-700 text-gray-300'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Mic className="w-5 h-5 text-purple-500" />
                  <span className="text-sm font-medium">Podcast</span>
                </label>

                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-gray-700 text-gray-300'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <Smile className="w-5 h-5 text-yellow-500" />
                </button>
              </div>

              <button
                type="button"
                className="flex items-center space-x-2 px-6 py-2 bg-[#40E0D0] hover:bg-[#35c5b5] text-white font-semibold rounded-lg transition-colors"
              >
                <Send className="w-4 h-4" />
                <span>Post</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Create a Post
              </h3>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className={`p-2 rounded-full transition-colors ${
                  theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <ChevronUp className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Share your Workday insights, ask questions, or connect with fellow consultants..."
                className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] transition-all resize-none ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
                }`}
                rows={4}
              />
            </div>

            {mediaPreviews.length > 0 && (
              <div className="mb-4 space-y-3">
                {mediaPreviews.map((preview, index) => (
                  <div key={index} className={`rounded-lg border-2 p-3 ${theme === 'dark' ? 'border-gray-600 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {postType === 'image'
                          ? (adjustingIndex === index ? 'Drag to reposition' : 'Photo preview')
                          : postType === 'video' ? 'Video preview' : 'File attached'}
                      </p>
                      <div className="flex items-center gap-2">
                        {postType === 'image' && adjustingIndex !== index && (
                          <button
                            type="button"
                            onClick={() => setAdjustingIndex(index)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-[#40E0D0] hover:text-[#35c5b5] transition-colors"
                          >
                            <Move className="w-3.5 h-3.5" />
                            Adjust
                          </button>
                        )}
                        {postType === 'image' && adjustingIndex === index && (
                          <button
                            type="button"
                            onClick={() => setAdjustingIndex(null)}
                            className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            Done
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeMedia(index)}
                          className="p-1 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {postType === 'image' ? (
                      <div
                        ref={el => { mediaDragRefs.current[index] = el; }}
                        className={`relative rounded-lg overflow-hidden ${adjustingIndex === index ? 'cursor-grab active:cursor-grabbing ring-2 ring-[#40E0D0]' : 'cursor-default'}`}
                        style={{ height: '220px' }}
                        onMouseDown={adjustingIndex === index ? (e) => handleMediaDragStart(e, index) : undefined}
                        onTouchStart={adjustingIndex === index ? (e) => handleMediaDragStart(e, index) : undefined}
                      >
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-full object-cover select-none pointer-events-none"
                          style={{ objectPosition: `${mediaPositions[index]?.x ?? 50}% ${mediaPositions[index]?.y ?? 50}%` }}
                          draggable={false}
                        />
                        {adjustingIndex === index && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="bg-black/30 rounded-full p-3">
                              <Move className="w-6 h-6 text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : postType === 'video' ? (
                      <video src={preview} className="w-full h-48 object-cover rounded-lg" controls />
                    ) : (
                      <div className={`w-full h-24 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <FileText className="w-10 h-10 text-gray-400" />
                      </div>
                    )}
                    {postType === 'image' && adjustingIndex !== index && (
                      <p className={`text-xs mt-1.5 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                        Click "Adjust" if the crop doesn't show the right area.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className={`mb-4 p-3 rounded-lg border-2 ${
              theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
            }`}>
              <p className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Post Visibility
              </p>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setVisibility('community')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                    visibility === 'community'
                      ? 'bg-[#40E0D0] text-white'
                      : theme === 'dark'
                      ? 'bg-gray-800 text-gray-400 hover:bg-gray-600'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  <span className="font-medium">Community Only</span>
                </button>
                <button
                  type="button"
                  onClick={() => setVisibility('public')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                    visibility === 'public'
                      ? 'bg-[#40E0D0] text-white'
                      : theme === 'dark'
                      ? 'bg-gray-800 text-gray-400 hover:bg-gray-600'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  <Globe className="w-4 h-4" />
                  <span className="font-medium">Public</span>
                </button>
              </div>
              <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                {visibility === 'community'
                  ? 'Only consultants who signed up can see this post'
                  : 'Everyone, including non-members, can see this post'}
              </p>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center space-x-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleFileSelect(e, 'image')}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-gray-700 text-gray-300'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <ImageIcon className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium">Photo</span>
                </label>

                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => handleFileSelect(e, 'video')}
                  className="hidden"
                  id="video-upload"
                />
                <label
                  htmlFor="video-upload"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-gray-700 text-gray-300'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <Video className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium">Video</span>
                </label>

                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => handleFileSelect(e, 'podcast')}
                  className="hidden"
                  id="audio-upload"
                />
                <label
                  htmlFor="audio-upload"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-gray-700 text-gray-300'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <Mic className="w-5 h-5 text-purple-500" />
                  <span className="text-sm font-medium">Podcast</span>
                </label>

                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-gray-700 text-gray-300'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <Smile className="w-5 h-5 text-yellow-500" />
                </button>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || (!content.trim() && mediaFiles.length === 0)}
                className="flex items-center space-x-2 px-6 py-2 bg-[#40E0D0] hover:bg-[#35c5b5] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    <span>Posting...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Post</span>
                  </>
                )}
              </button>
            </div>

            {showEmojiPicker && (
              <div className={`mt-3 p-3 rounded-lg ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
              }`}>
                <div className="flex flex-wrap gap-2">
                  {commonEmojis.map((emoji, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        setContent(content + emoji);
                        setShowEmojiPicker(false);
                      }}
                      className="text-2xl hover:scale-125 transition-transform"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  );
}
